"use client";

import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  calculateDxfCosts,
  getAvailableMaterials,
  getAvailablePlatforms,
} from "@/lib/price-calculator-dxf"; // Placeholder for DXF cost calculation
import {
  ContactShadows,
  OrbitControls,
  OrthographicCamera,
} from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import {
  ACESFilmicToneMapping,
  AxesHelper,
  Box3,
  Color,
  Group,
  Line,
  LineSegments,
  Material,
  Mesh,
  OrthographicCamera as ThreeOrthographicCamera,
  Vector3,
} from "three"; // Added Mesh, Material, BufferGeometry, Line, LineSegments, AxesHelper, BoxHelper, Color, LineBasicMaterial, Box3, Vector3
import { DXFViewer } from "three-dxf-viewer"; // Import the new library
import { OrbitControls as OrbitControlsImpl } from "three-stdlib"; // Import the implementation type
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

// Removed DxfEntity and DxfParseResult interfaces as they are no longer needed

interface DxfViewerProps {
  file: File;
  showPrice: boolean;
}

// Removed the old DxfModel component

// Modified LoadedDxfModel component to calculate bounds and force color
function LoadedDxfModel({
  file,
  onLoaded,
}: {
  file: File;
  onLoaded: (result: {
    dxf: Group | null;
    center: Vector3 | null;
    size: Vector3 | null;
  }) => void;
}) {
  const [dxf, setDxf] = useState<Group | null>(null);
  const objectRef = useRef<Group>(null);

  useEffect(() => {
    let isMounted = true;
    setDxf(null);
    onLoaded({ dxf: null, center: null, size: null });

    if (!file) return;

    const loadDxf = async () => {
      let loadedDxf: Group | null = null;
      try {
        const fontPath = "/fonts/helvetiker_regular.typeface.json";
        const viewer = new DXFViewer();
        loadedDxf = await viewer.getFromFile(file, fontPath);

        console.log("Raw object returned by three-dxf-viewer:", loadedDxf);

        if (isMounted) {
          if (
            !loadedDxf ||
            !loadedDxf.children ||
            loadedDxf.children.length === 0
          ) {
            console.warn("Loaded DXF object appears to be empty or invalid");
            setDxf(null);
            onLoaded({ dxf: null, center: null, size: null });
          } else {
            // Calculate path length before any transformations
            let totalLength = 0;
            loadedDxf.traverse((child) => {
              if (child instanceof Line || child instanceof LineSegments) {
                const geometry = child.geometry;
                const positions = geometry.attributes.position.array;

                if (child instanceof LineSegments) {
                  for (let i = 0; i < positions.length; i += 6) {
                    const x1 = positions[i];
                    const y1 = positions[i + 1];
                    const x2 = positions[i + 3];
                    const y2 = positions[i + 4];
                    const dx = x2 - x1;
                    const dy = y2 - y1;
                    totalLength += Math.sqrt(dx * dx + dy * dy);
                  }
                } else if (child instanceof Line) {
                  for (let i = 0; i < positions.length - 3; i += 3) {
                    const x1 = positions[i];
                    const y1 = positions[i + 1];
                    const x2 = positions[i + 3];
                    const y2 = positions[i + 4];
                    const dx = x2 - x1;
                    const dy = y2 - y1;
                    totalLength += Math.sqrt(dx * dx + dy * dy);
                  }
                }
              }
            });

            console.log("Total path length (mm):", totalLength);

            // Apply visual styling
            loadedDxf.traverse((child) => {
              let material: Material | Material[] | undefined;
              if (child instanceof Line || child instanceof LineSegments) {
                material = child.material;
              } else if (child instanceof Mesh) {
                material = child.material;
              }
              if (material) {
                const materials = Array.isArray(material)
                  ? material
                  : [material];
                materials.forEach((mat) => {
                  if ("color" in mat && mat.color instanceof Color) {
                    mat.color.set(0x000000);
                  }
                  mat.needsUpdate = true;
                });
              }
            });

            // Calculate bounds and center
            const box = new Box3().setFromObject(loadedDxf);
            const size = new Vector3();
            box.getSize(size);
            const center = new Vector3();
            box.getCenter(center);

            // Apply positioning
            loadedDxf.position.set(-center.x, -box.min.z, -center.y);

            console.log("Calculated Bounds - Size:", size, "Center:", center);

            setDxf(loadedDxf);
            onLoaded({ dxf: loadedDxf, center: null, size });
          }
        }
      } catch (error) {
        console.error("Error during DXF loading/processing:", error);
        if (isMounted) {
          setDxf(null);
          onLoaded({ dxf: null, center: null, size: null });
        }
      }
    };

    loadDxf();

    return () => {
      isMounted = false;
      if (dxf) {
        const objToClean = objectRef.current || dxf;
        objToClean.traverse((child) => {
          if (child instanceof Mesh) {
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
              const materials = Array.isArray(child.material)
                ? child.material
                : [child.material];
              materials.forEach((mat) => mat.dispose());
            }
          } else if (child instanceof LineSegments || child instanceof Line) {
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
              const materials = Array.isArray(child.material)
                ? child.material
                : [child.material];
              materials.forEach((mat) => mat.dispose());
            }
          }
        });
      }
      setDxf(null);
      onLoaded({ dxf: null, center: null, size: null });
    };
  }, [file]);

  return dxf ? (
    <primitive object={dxf} ref={objectRef} rotation={[83.25, 0, 0]} />
  ) : null;
}

export const DxfViewer: React.FC<DxfViewerProps> = ({ file, showPrice }) => {
  const [priceDetails, setPriceDetails] = useState<ReturnType<
    typeof calculateDxfCosts
  > | null>(null);
  const [loadedData, setLoadedData] = useState<{
    dxf: Group | null;
    center: Vector3 | null;
    size: Vector3 | null;
  }>({ dxf: null, center: null, size: null });
  const [isCalculating, setIsCalculating] = useState(false);

  const [selectedPlatform, setSelectedPlatform] = useState<string>("laser");
  const [selectedMaterial, setSelectedMaterial] =
    useState<string>("mild_steel");

  const availablePlatforms = useMemo(() => getAvailablePlatforms(), []);
  const availableMaterials = useMemo(() => getAvailableMaterials(), []);

  const axesRef = useRef<AxesHelper>(null);
  const cameraRef = useRef<ThreeOrthographicCamera>(null!);
  const controlsRef = useRef<OrbitControlsImpl>(null!);

  // Update state when model is loaded
  const handleLoaded = (result: {
    dxf: Group | null;
    center: Vector3 | null;
    size: Vector3 | null;
  }) => {
    setLoadedData(result);
  };

  // Effect to update camera based on loaded model bounds
  useEffect(() => {
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    if (!camera || !controls || !controls.domElement) return;

    if (loadedData.size) {
      const { size } = loadedData;
      const PADDING_FACTOR = 1.2;
      const objectWidth = size.x * PADDING_FACTOR;
      const objectHeight = size.y * PADDING_FACTOR;

      const dom = controls.domElement;
      const cw = dom.clientWidth;
      const ch = dom.clientHeight;
      if (cw === 0 || ch === 0 || objectWidth === 0 || objectHeight === 0) {
        console.warn("Cannot calculate zoom: Zero dimensions detected.");
        return;
      }

      const fitZoomWidth = cw / objectWidth;
      const fitZoomHeight = ch / objectHeight;
      camera.zoom = Math.min(fitZoomWidth, fitZoomHeight);
      camera.zoom = Math.max(0.1, camera.zoom);
      camera.zoom = Math.min(500, camera.zoom);

      const maxDim = Math.max(size.x, size.y, size.z);
      const distance = maxDim * 1.5;
      camera.position.set(0, distance, 0);
      controls.target.set(0, 0, 0);

      camera.updateProjectionMatrix();
      controls.update();
    } else {
      // Reset camera on unload
      controls.target.set(0, 0, 0);
      camera.position.set(0, 50, 0);
      camera.zoom = 30;
      camera.updateProjectionMatrix();
      controls.reset();
      controls.update();
    }
  }, [loadedData.size, cameraRef, controlsRef]);

  useEffect(() => {
    if (loadedData.dxf) {
      setIsCalculating(true);
      try {
        const costs = calculateDxfCosts(
          loadedData.dxf,
          selectedPlatform,
          selectedMaterial
        );
        setPriceDetails(costs);
      } catch (error) {
        console.error("Error calculating DXF costs:", error);
        setPriceDetails(null);
      } finally {
        setIsCalculating(false);
      }
    } else {
      setPriceDetails(null);
    }
  }, [loadedData.dxf, selectedPlatform, selectedMaterial]);

  useEffect(() => {
    // Reset when file changes
    setLoadedData({ dxf: null, center: null, size: null });
    setPriceDetails(null);
  }, [file]);

  return (
    <div className="space-y-4">
      <div className="w-full h-[400px] rounded-lg overflow-hidden relative border border-input shadow-sm">
        <Canvas
          className="absolute inset-0"
          gl={{
            antialias: true,
            toneMapping: ACESFilmicToneMapping,
            powerPreference: "high-performance",
          }}
          onCreated={({ gl }) => {
            // Explicitly set clear color to white, fully opaque
            gl.setClearColor("#ffffff", 1.0);
          }}
          dpr={[1, 2]}
        >
          <OrthographicCamera
            ref={cameraRef}
            makeDefault
            zoom={30}
            position={[0, 50, 0]}
            near={0.1}
            far={2000}
          />
          <ambientLight intensity={0.4} />
          <hemisphereLight
            color={0xffffff}
            groundColor={0xffffff}
            intensity={0.1}
          />
          <directionalLight
            color={0xffffff}
            position={[3, 3, 3]}
            intensity={0.1}
          />
          {loadedData.size && (
            <gridHelper
              args={[
                Math.max(loadedData.size.x, loadedData.size.y),
                20,
                "#e0e0e0",
                "#c0c0c0",
              ]}
              position={[0, -0.01, 0]}
            />
          )}
          <ContactShadows
            frames={1}
            position={[0, -0.01, 0]}
            blur={2}
            opacity={0.4}
            far={1}
          />
          <axesHelper ref={axesRef} args={[3]} />
          <Suspense fallback={null}>
            {file && <LoadedDxfModel file={file} onLoaded={handleLoaded} />}
          </Suspense>
          <OrbitControls
            ref={controlsRef}
            enableDamping
            dampingFactor={0.1}
            minPolarAngle={0}
            maxPolarAngle={Math.PI / 2.1}
            enablePan={true}
            enableRotate={true}
            zoomSpeed={1.0}
            panSpeed={0.8}
            target={[0, 0, 0]}
          />
        </Canvas>
      </div>

      <div className="grid grid-cols-2 gap-4 p-4 border rounded-md bg-muted/20">
        <div>
          <Label htmlFor="dxf-platform-select" className="mb-2">
            Cutting Platform
          </Label>
          <Select
            value={selectedPlatform}
            onValueChange={(value) => {
              setSelectedPlatform(value);
              setIsCalculating(true);
            }}
            name="dxf-platform-select"
          >
            <SelectTrigger id="dxf-platform-select">
              <SelectValue placeholder="Select platform" />
            </SelectTrigger>
            <SelectContent>
              {availablePlatforms.map((platform) => (
                <SelectItem key={platform} value={platform}>
                  {platform.charAt(0).toUpperCase() + platform.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="dxf-material-select" className="mb-2">
            Material
          </Label>
          <Select
            value={selectedMaterial}
            onValueChange={(value) => {
              setSelectedMaterial(value);
              setIsCalculating(true);
            }}
            name="dxf-material-select"
          >
            <SelectTrigger id="dxf-material-select">
              <SelectValue placeholder="Select material" />
            </SelectTrigger>
            <SelectContent>
              {availableMaterials.map((material) => (
                <SelectItem key={material} value={material}>
                  {material
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {showPrice && priceDetails && !isCalculating && (
        <Card>
          <CardHeader>
            <CardTitle>
              Price Estimate (
              {selectedMaterial
                .split("_")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ")}{" "}
              -{" "}
              {selectedPlatform.charAt(0).toUpperCase() +
                selectedPlatform.slice(1)}
              )
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Path Length:</span>
              <span>{priceDetails.totalLength.toFixed(2)} mm</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                Estimated Cutting Time:
              </span>
              <span>{priceDetails.cuttingTime.toFixed(1)} mins</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Material Cost:</span>
              <span>${priceDetails.materialCost.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Setup Cost:</span>
              <span>${priceDetails.setupCost.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Power Cost:</span>
              <span>${priceDetails.powerCost.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-semibold border-t pt-2 mt-2">
              <span>Total Estimated Cost:</span>
              <span>${priceDetails.totalCost.toFixed(2)}</span>
            </div>
          </CardContent>
        </Card>
      )}
      {showPrice && (isCalculating || (!priceDetails && loadedData.dxf)) && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-4">
              <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-center text-muted-foreground">
                Calculating price for{" "}
                {selectedMaterial
                  .split("_")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ")}{" "}
                using{" "}
                {selectedPlatform.charAt(0).toUpperCase() +
                  selectedPlatform.slice(1)}
                ...
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
